import React from 'react';

import type { EventDto } from '../../api/types';
import { computeUserDayStatuses, type UserDayStatus } from '../../domain/calendar/userDayStatus';
import { useAppSelector } from '../../store/hooks';
import { addDays, formatDateYYYYMMDD } from '../../utils/date';

import { addMonths, buildMonthGrid, isSameDay, isWeekend, startOfMonth, type CalendarMode } from './calendar';

type CalendarPickerProps = {
  mode: CalendarMode;
  selectedDate: Date;
  onChangeSelectedDate: (d: Date) => void;
  onToggleMode: () => void;
  events?: EventDto[];
};

function DayIndicators({ dateKey, statuses, isPast }: {
  dateKey: string;
  statuses: Map<string, UserDayStatus>;
  isPast: boolean;
}) {
  if (isPast) return null;

  const status = statuses.get(dateKey);
  if (!status || (!status.hasConfirmed && !status.hasPending)) return null;

  return (
    <div className="day-indicators">
      {status.hasConfirmed && <span className="day-indicator day-indicator-confirmed" />}
      {status.hasPending && <span className="day-indicator day-indicator-pending" />}
    </div>
  );
}

export function CalendarPicker({
  mode,
  selectedDate,
  onChangeSelectedDate,
  onToggleMode,
  events,
}: CalendarPickerProps) {
  const today = React.useMemo(() => new Date(), []);
  const telegramId = useAppSelector((s) => s.auth.user?.telegramId);

  const userDayStatuses = React.useMemo(() => {
    return computeUserDayStatuses(events ?? [], telegramId);
  }, [events, telegramId]);

  const startOfDay = React.useCallback((d: Date) => {
    const next = new Date(d);
    next.setHours(0, 0, 0, 0);
    return next;
  }, []);

  const getWeekStartForSelectedDate = React.useCallback((d: Date) => startOfDay(d), [startOfDay]);

  const [month, setMonth] = React.useState(() => startOfMonth(selectedDate));
  const [weekStart, setWeekStart] = React.useState(() => getWeekStartForSelectedDate(selectedDate));

  const prevModeRef = React.useRef<CalendarMode>(mode);

  React.useEffect(() => {
    if (mode === 'month') {
      setMonth(startOfMonth(selectedDate));
    }
  }, [mode, selectedDate]);

  React.useEffect(() => {
    const prevMode = prevModeRef.current;
    prevModeRef.current = mode;

    if (mode !== 'week') return;

    // When switching to week view, center the selected date.
    if (prevMode !== 'week') {
      setWeekStart(getWeekStartForSelectedDate(selectedDate));
      return;
    }

    // When staying in week view, keep the 7-day window stable.
    // Only recenter if the selected date is outside the current window.
    const selected = startOfDay(selectedDate).getTime();
    const start = startOfDay(weekStart).getTime();
    const end = startOfDay(addDays(weekStart, 6)).getTime();
    if (selected < start || selected > end) {
      setWeekStart(getWeekStartForSelectedDate(selectedDate));
    }
  }, [mode, selectedDate, weekStart, startOfDay, getWeekStartForSelectedDate]);

  if (mode === 'week') {
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const todayStart = startOfDay(today).getTime();
    const todayWeekStart = getWeekStartForSelectedDate(today);
    
    // Can go back only if we've already navigated forward from current week
    const canGoBack = weekStart.getTime() > todayWeekStart.getTime();

    const goToPrevWeek = () => {
      if (!canGoBack) return;
      const newWeekStart = addDays(weekStart, -7);
      setWeekStart(newWeekStart);
      onChangeSelectedDate(newWeekStart);
    };

    const goToNextWeek = () => {
      const newWeekStart = addDays(weekStart, 7);
      setWeekStart(newWeekStart);
      onChangeSelectedDate(newWeekStart);
    };

    return (
      <div className="calendar-container">
        <div className="calendar-header">
          {canGoBack && (
            <button
              type="button"
              onClick={goToPrevWeek}
              className="calendar-nav-arrow"
            >
              ←
            </button>
          )}
          <div className="calendar-title calendar-title-center">
            {weekStart.toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' })} — {addDays(weekStart, 6).toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' })}
          </div>
          <div className="calendar-controls">
            <button
              type="button"
              onClick={onToggleMode}
              className="calendar-toggle"
            >
              📅 Месяц
            </button>
            <button
              type="button"
              onClick={goToNextWeek}
              className="calendar-nav-arrow"
            >
              →
            </button>
          </div>
        </div>

        <div className="calendar-days">
          {days.map((d) => {
            const selected = isSameDay(d, selectedDate);
            const weekend = isWeekend(d);
            const isToday = isSameDay(d, today);
            const isPast = startOfDay(d).getTime() < todayStart;

            const dateKey = formatDateYYYYMMDD(d);

            return (
              <button
                key={dateKey}
                type="button"
                onClick={() => onChangeSelectedDate(d)}
                className={[
                  'calendar-day',
                  selected ? 'active' : '',
                  isToday ? 'today' : '',
                  weekend ? 'weekend' : '',
                  isPast ? 'past' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <DayIndicators dateKey={dateKey} statuses={userDayStatuses} isPast={isPast} />
                <div className="day-label">
                  {d.toLocaleDateString('ru-RU', { weekday: 'short' })}
                </div>
                <div className="day-number">{d.getDate()}</div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const days = buildMonthGrid(month);
  const todayStart = startOfDay(today).getTime();

  return (
    <div className="calendar-container">
      <div className="calendar-month-header">
        <button
          type="button"
          onClick={() => setMonth((m) => addMonths(m, -1))}
          className="calendar-nav-arrow"
        >
          ←
        </button>
        <span>{month.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}</span>
        <button
          type="button"
          onClick={() => setMonth((m) => addMonths(m, 1))}
          className="calendar-nav-arrow"
        >
          →
        </button>
      </div>

      <div className="calendar-days">
        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((x) => (
          <div key={x} className="day-label">
            {x}
          </div>
        ))}

        {days.map((d) => {
          const selected = isSameDay(d, selectedDate);
          const weekend = isWeekend(d);
          const isToday = isSameDay(d, today);
          const inMonth = d.getMonth() === month.getMonth();
          const isPast = startOfDay(d).getTime() < todayStart;
          const dateKey = formatDateYYYYMMDD(d);

          return (
            <button
              key={dateKey}
              type="button"
              onClick={() => onChangeSelectedDate(d)}
              className={[
                'calendar-day',
                selected ? 'active' : '',
                isToday ? 'today' : '',
                weekend ? 'weekend' : '',
                inMonth ? '' : 'out-month',
                isPast ? 'past' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <DayIndicators dateKey={dateKey} statuses={userDayStatuses} isPast={isPast} />
              <div className="day-number">{d.getDate()}</div>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onToggleMode}
        className="calendar-toggle calendar-month-toggle"
      >
        📅 Неделя
      </button>
    </div>
  );
}
